import { apiErrorCode, extractApiError } from "../../lib/apiErrors";

/**
 * Every refusal code the Tenant 360 finance routes answer
 * (docs/auth-and-admin/super-admin-s7-tenant360-contract.md section 3.1 and, for the custom price,
 * docs/auth-and-admin/super-admin-d4-customprice-contract.md section 8.5), each with a sentence
 * `t360f.error.<CODE>` written in fr/en/es.
 */
export const FINANCE_ERROR_CODES = [
  "TENANT_NOT_FOUND",
  "REBATE_NO_PAYMENT_ACCOUNT",
  "REBATE_UNVERIFIED",
  "REBATE_FAILED",
  "EFFECTIVE_UNTIL_NOT_FUTURE",
  "EFFECTIVE_UNTIL_OUT_OF_RANGE",
  "EFFECTIVE_UNTIL_BEFORE_NEXT_INVOICE",
  "NO_BILLING_SUBSCRIPTION",
  "SUBSCRIPTION_ENDING",
  "CUSTOM_PRICE_ON_OTHER_SUBSCRIPTION",
  "BILLING_CURRENCY_MISMATCH",
  "BILLING_CHANGE_IN_PROGRESS",
  "BILLING_CHANGE_SUPERSEDED",
  "NO_CUSTOM_PRICE",
  "STRIPE_NOT_CONFIGURED",
  "CUSTOM_PRICE_REFUSED",
  "CUSTOM_PRICE_UNVERIFIED",
  "CUSTOM_PRICE_RESTORE_REFUSED",
  "CUSTOM_PRICE_RESTORE_UNVERIFIED",
  "TX_NOT_FOUND",
  "TX_NOT_REFUNDABLE",
  "REFUND_EXCEEDS_BALANCE",
  "REFUND_NO_PAYMENT_REFERENCE",
  "REFUND_FAILED",
  "REFUND_UNVERIFIED",
];

/**
 * Refusals after which sending the same action again cannot help or could apply money twice:
 * a rebate with no Stripe account to credit, the outcomes Stripe never confirmed (the server holds
 * every further price change until it has checked), and the custom-price preconditions that the
 * same form cannot satisfy (no live subscription, one that is ending, a custom price still on
 * another subscription that must be removed first, a currency that differs from the
 * subscription's, no Stripe key). The modal keeps its Confirm button disabled for the
 * rest of its life after one of these.
 */
export const RETRY_BLOCKING_CODES = [
  "REBATE_NO_PAYMENT_ACCOUNT", "REBATE_UNVERIFIED", "REFUND_UNVERIFIED",
  "NO_BILLING_SUBSCRIPTION", "SUBSCRIPTION_ENDING", "BILLING_CURRENCY_MISMATCH", "STRIPE_NOT_CONFIGURED",
  "CUSTOM_PRICE_ON_OTHER_SUBSCRIPTION", "CUSTOM_PRICE_UNVERIFIED", "CUSTOM_PRICE_RESTORE_UNVERIFIED",
];

/**
 * Custom-price refusals that say the page is out of date (another change is under way or just
 * landed, the price is already gone) or that the server now holds an unconfirmed Stripe call the
 * page must show as pending: the page re-reads `/360` after one of these.
 */
export const RELOAD_CODES = [
  "BILLING_CHANGE_IN_PROGRESS", "BILLING_CHANGE_SUPERSEDED", "NO_CUSTOM_PRICE",
  "CUSTOM_PRICE_UNVERIFIED", "CUSTOM_PRICE_RESTORE_UNVERIFIED",
];

/**
 * Refund refusals that mean the payment chosen from the refundable list no longer matches what
 * the server holds (its balance shrank, it stopped being refundable, or it is gone). The modal
 * drops the choice and re-reads the list after one of these.
 */
export const STALE_REFUND_CODES = ["REFUND_EXCEEDS_BALANCE", "TX_NOT_REFUNDABLE", "TX_NOT_FOUND"];

/** The sentence for a finance error code; a code this page does not know says the generic one. */
export const financeCodeText = (code, t) =>
  t(FINANCE_ERROR_CODES.includes(code) ? `t360f.error.${code}` : "t360f.error.UNKNOWN");

/**
 * A finance refusal, said in the reader's language — the twin of `saas/trial/errorText.js`.
 *
 * The wire carries `detail = {code, message}` and `message` is English developer text, never
 * rendered:
 *   - a code in `FINANCE_ERROR_CODES` -> its sentence; any other code -> `t360f.error.UNKNOWN`;
 *   - no code at all (a dropped connection, an HTML 500, pydantic's 422 array) -> `extractApiError`.
 *
 * @param {any} e the axios error
 * @param {{t: function, fallback?: string}} opts
 * @returns {string}
 */
export function financeErrorText(e, { t, fallback = "" }) {
  const code = apiErrorCode(e);
  return code ? financeCodeText(code, t) : extractApiError(e, fallback, t);
}

export default financeErrorText;
